import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Loader2,
  CircleDot,
  CheckCircle2,
  Circle,
  Pencil,
  Map,
  Brain,
  Calendar,
  Sparkles,
  X,
  Save,
  Download,
  Upload,
} from "lucide-react";
import "./AdminRoadmap.css";

const API = process.env.REACT_APP_BACKEND_URL;

const STREAMS = [
  {
    key: "main",
    label: "Social Go & Pro",
    sub: "Main Roadmap",
    Icon: Map,
    accent: "#96C11F",
    accentDim: "rgba(150, 193, 31, 0.22)",
  },
  {
    key: "derek",
    label: "Derek Development",
    sub: "AI Employee",
    Icon: Brain,
    accent: "#a78bfa",
    accentDim: "rgba(167, 139, 250, 0.18)",
  },
  {
    key: "social_week",
    label: "Social Week",
    sub: "Planner",
    Icon: Calendar,
    accent: "#34d399",
    accentDim: "rgba(52, 211, 153, 0.18)",
  },
  {
    key: "future",
    label: "Future Improvements",
    sub: "Backlog",
    Icon: Sparkles,
    accent: "#60a5fa",
    accentDim: "rgba(96, 165, 250, 0.18)",
  },
];

const STATUS_ORDER = ["todo", "in_progress", "done"];
const STATUS_META = {
  todo: { label: "To Do", Icon: Circle, color: "#94a3b8" },
  in_progress: { label: "In Progress", Icon: CircleDot, color: "#96C11F" },
  done: { label: "Done", Icon: CheckCircle2, color: "#34d399" },
};

// Polar → Cartesian for SVG spider layout. Angle in degrees, 0 = top.
function polar(cx, cy, radius, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
}

export default function AdminRoadmap() {
  const [activeStream, setActiveStream] = useState("main");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ streams: {}, counts: {} });
  const [selectedSpokeId, setSelectedSpokeId] = useState(null);
  const [busy, setBusy] = useState(false);

  // New-spoke dialog
  const [showAddSpoke, setShowAddSpoke] = useState(false);
  const [newSpokeLabel, setNewSpokeLabel] = useState("");

  // Inline rename state
  const [renamingSpokeId, setRenamingSpokeId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");

  // New-task input (per selected spoke)
  const [newTaskTitle, setNewTaskTitle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/roadmap`);
      setData(res.data || { streams: {}, counts: {} });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load roadmap");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Export / Import (port roadmap between Preview and live) ──
  const fileInputRef = useRef(null);
  const [transferring, setTransferring] = useState(false);

  const handleExport = async () => {
    setTransferring(true);
    try {
      const res = await axios.get(`${API}/api/roadmap/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `nichecom-roadmap-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Roadmap exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setTransferring(false);
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected
    if (!file) return;
    if (!window.confirm(
      "This will REPLACE the entire roadmap in this environment with the contents of " +
      `"${file.name}". Continue?`
    )) return;

    setTransferring(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed.spokes) || !Array.isArray(parsed.tasks)) {
        throw new Error("Invalid roadmap file — missing spokes / tasks arrays.");
      }
      const res = await axios.post(`${API}/api/roadmap/import`, {
        spokes: parsed.spokes,
        tasks: parsed.tasks,
        replace: true,
      });
      const imp = res.data?.imported || {};
      toast.success(`Imported ${imp.spokes || 0} spokes & ${imp.tasks || 0} tasks`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Import failed";
      toast.error(msg);
    } finally {
      setTransferring(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const stream = useMemo(
    () => STREAMS.find((s) => s.key === activeStream) || STREAMS[0],
    [activeStream]
  );
  const spokes = useMemo(
    () => data?.streams?.[activeStream] || [],
    [data, activeStream]
  );
  const counts = data?.counts?.[activeStream] || {
    spokes: 0,
    tasks: 0,
    done: 0,
    in_progress: 0,
  };
  const ALL_KEY = "__all__";
  const showingAll = selectedSpokeId === ALL_KEY;
  const selectedSpoke = useMemo(
    () => (showingAll ? null : spokes.find((s) => s.id === selectedSpokeId) || null),
    [spokes, selectedSpokeId, showingAll]
  );

  // Auto-select first spoke when switching streams (or fall back to "All")
  useEffect(() => {
    if (spokes.length === 0) {
      setSelectedSpokeId(null);
    } else if (selectedSpokeId !== ALL_KEY && !spokes.find((s) => s.id === selectedSpokeId)) {
      setSelectedSpokeId(spokes[0].id);
    }
  }, [spokes, selectedSpokeId]);

  // ── Spoke ops ──
  const handleAddSpoke = async () => {
    const label = newSpokeLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/roadmap/spokes`, {
        stream: activeStream,
        label,
      });
      toast.success("Spoke added");
      setShowAddSpoke(false);
      setNewSpokeLabel("");
      await load();
      if (res.data?.spoke?.id) setSelectedSpokeId(res.data.spoke.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add spoke");
    } finally {
      setBusy(false);
    }
  };

  const handleRenameSpoke = async (spokeId) => {
    const label = renameDraft.trim();
    if (!label) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/api/roadmap/spokes/${spokeId}`, { label });
      toast.success("Renamed");
      setRenamingSpokeId(null);
      setRenameDraft("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSpoke = async (spokeId) => {
    if (!window.confirm("Delete this department and all its tasks?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/roadmap/spokes/${spokeId}`);
      toast.success("Deleted");
      if (selectedSpokeId === spokeId) setSelectedSpokeId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  // ── Task ops ──
  const handleAddTask = async () => {
    if (!selectedSpoke) return;
    const title = newTaskTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/roadmap/tasks`, {
        spoke_id: selectedSpoke.id,
        title,
      });
      setNewTaskTitle("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add task");
    } finally {
      setBusy(false);
    }
  };

  const cycleTaskStatus = async (task) => {
    const nextIdx = (STATUS_ORDER.indexOf(task.status) + 1) % STATUS_ORDER.length;
    const next = STATUS_ORDER[nextIdx];
    try {
      await axios.patch(`${API}/api/roadmap/tasks/${task.id}`, { status: next });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/api/roadmap/tasks/${taskId}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ── SVG geometry ──
  // Canvas is 720x720; the diagram occupies the full canvas
  const VB = 720;
  const cx = VB / 2;
  const cy = VB / 2;
  const HUB_R = 78;
  const SPOKE_LEN = 250; // distance from centre to spoke node centre
  const NODE_R = 44;
  const angleFor = (idx, total) => (idx * 360) / Math.max(total, 1);

  return (
    <div className="roadmap-shell" data-testid="roadmap-shell">
      <div className="roadmap-header">
        <div>
          <h2 className="roadmap-title">
            <Map size={18} /> Roadmap
          </h2>
          <p className="roadmap-sub">
            Internal planning board — visualise and steer the build.
          </p>
        </div>
        <div className="roadmap-stats">
          <div className="rm-stat">
            <span className="rm-stat-count">{counts.tasks}</span>
            <span className="rm-stat-label">Tasks</span>
          </div>
          <div className="rm-stat rm-stat-progress">
            <span className="rm-stat-count">{counts.in_progress}</span>
            <span className="rm-stat-label">In progress</span>
          </div>
          <div className="rm-stat rm-stat-done">
            <span className="rm-stat-count">{counts.done}</span>
            <span className="rm-stat-label">Done</span>
          </div>

          <div className="rm-transfer">
            <button
              type="button"
              className="rm-transfer-btn"
              onClick={handleExport}
              disabled={transferring}
              title="Export roadmap as JSON — restore in another environment via Import"
              data-testid="roadmap-export-btn"
            >
              {transferring ? <Loader2 size={13} className="rm-spin" /> : <Download size={13} />}
              <span>Export</span>
            </button>
            <button
              type="button"
              className="rm-transfer-btn"
              onClick={handleImportClick}
              disabled={transferring}
              title="Import roadmap JSON — replaces everything in this environment"
              data-testid="roadmap-import-btn"
            >
              {transferring ? <Loader2 size={13} className="rm-spin" /> : <Upload size={13} />}
              <span>Import</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              style={{ display: "none" }}
              data-testid="roadmap-import-file"
            />
          </div>
        </div>
      </div>

      <div className="roadmap-tabs" data-testid="roadmap-tabs" role="tablist">
        {STREAMS.map((s) => {
          const active = activeStream === s.key;
          const c = data?.counts?.[s.key] || { tasks: 0 };
          return (
            <button
              key={s.key}
              role="tab"
              className={`rm-tab ${active ? "active" : ""}`}
              style={active ? { "--rm-accent": s.accent } : undefined}
              onClick={() => setActiveStream(s.key)}
              data-testid={`roadmap-tab-${s.key}`}
            >
              <s.Icon size={15} />
              <span className="rm-tab-label">{s.label}</span>
              <span className="rm-tab-count">{c.tasks}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="roadmap-loading">
          <Loader2 size={18} className="rm-spin" /> Loading…
        </div>
      ) : (
        <div
          className="roadmap-board"
          style={{ "--rm-accent": stream.accent, "--rm-accent-dim": stream.accentDim }}
          data-testid="roadmap-board"
        >
          {/* ─── SVG Spider Diagram ─── */}
          <div className="rm-diagram-wrap">
            <svg
              viewBox={`0 0 ${VB} ${VB}`}
              className="rm-diagram"
              data-testid="roadmap-diagram"
              role="img"
              aria-label={`${stream.label} roadmap diagram`}
              key={activeStream /* re-mount → re-animate */}
            >
              <defs>
                <radialGradient id="rm-hub-bg" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={stream.accent} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={stream.accent} stopOpacity="0" />
                </radialGradient>
                <radialGradient id="rm-hub-fill" cx="50%" cy="40%" r="60%">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </radialGradient>
              </defs>

              {/* Background ring pulses */}
              <circle cx={cx} cy={cy} r={SPOKE_LEN + NODE_R + 8} className="rm-orbit" />
              <circle cx={cx} cy={cy} r={SPOKE_LEN - 18} className="rm-orbit rm-orbit-inner" />
              <circle cx={cx} cy={cy} r={HUB_R + 18} className="rm-orbit rm-orbit-inner" />

              {/* Halo behind hub */}
              <circle cx={cx} cy={cy} r={HUB_R + 60} fill="url(#rm-hub-bg)" />

              {/* Spokes */}
              {spokes.map((sp, idx) => {
                const angle = angleFor(idx, spokes.length);
                const start = polar(cx, cy, HUB_R - 2, angle);
                const end = polar(cx, cy, SPOKE_LEN - NODE_R + 2, angle);
                const nodePos = polar(cx, cy, SPOKE_LEN, angle);
                const tasks = sp.tasks || [];
                const doneCount = tasks.filter((t) => t.status === "done").length;
                const progressCount = tasks.filter((t) => t.status === "in_progress").length;
                const total = tasks.length;
                const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;
                const isSelected = sp.id === selectedSpokeId;
                const delay = 0.08 + idx * 0.05;

                return (
                  <g
                    key={sp.id}
                    className={`rm-spoke ${isSelected ? "rm-spoke-active" : ""}`}
                    onClick={() => setSelectedSpokeId(sp.id)}
                    data-testid={`roadmap-spoke-${sp.id}`}
                    style={{ "--rm-spoke-delay": `${delay}s` }}
                  >
                    {/* Line from hub to node */}
                    <line
                      x1={start.x}
                      y1={start.y}
                      x2={end.x}
                      y2={end.y}
                      className="rm-line"
                    />
                    {/* Animated pulse line on top */}
                    {progressCount > 0 && (
                      <line
                        x1={start.x}
                        y1={start.y}
                        x2={end.x}
                        y2={end.y}
                        className="rm-line-pulse"
                      />
                    )}
                    {/* Node */}
                    <g className="rm-node-group" style={{ transformOrigin: `${nodePos.x}px ${nodePos.y}px` }}>
                      <circle
                        cx={nodePos.x}
                        cy={nodePos.y}
                        r={NODE_R + 4}
                        className="rm-node-halo"
                      />
                      <circle
                        cx={nodePos.x}
                        cy={nodePos.y}
                        r={NODE_R}
                        className="rm-node"
                      />
                      {/* Progress ring */}
                      {total > 0 && (
                        <circle
                          cx={nodePos.x}
                          cy={nodePos.y}
                          r={NODE_R}
                          className="rm-node-progress"
                          strokeDasharray={`${(pct / 100) * 2 * Math.PI * NODE_R} ${2 * Math.PI * NODE_R}`}
                          transform={`rotate(-90 ${nodePos.x} ${nodePos.y})`}
                        />
                      )}
                      {/* Task count in centre of node */}
                      <text
                        x={nodePos.x}
                        y={nodePos.y + 1}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="rm-node-count"
                      >
                        {total}
                      </text>
                    </g>
                    {/* Label outside node */}
                    <SpokeLabel
                      cx={cx}
                      cy={cy}
                      angle={angle}
                      radius={SPOKE_LEN + NODE_R + 14}
                      label={sp.label}
                    />
                  </g>
                );
              })}

              {/* Hub — clickable, shows all tasks across the stream */}
              <g
                className={`rm-hub ${showingAll ? "rm-hub-active" : ""}`}
                onClick={() => setSelectedSpokeId(ALL_KEY)}
                data-testid="roadmap-hub"
                style={{ cursor: "pointer" }}
              >
                <circle cx={cx} cy={cy} r={HUB_R} className="rm-hub-ring" />
                <circle cx={cx} cy={cy} r={HUB_R} fill="url(#rm-hub-fill)" />
                <text
                  x={cx}
                  y={cy - 6}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="rm-hub-title"
                >
                  {stream.label}
                </text>
                <text
                  x={cx}
                  y={cy + 14}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="rm-hub-sub"
                >
                  {showingAll ? "Showing all" : stream.sub}
                </text>
              </g>

              {spokes.length === 0 && (
                <text
                  x={cx}
                  y={cy + HUB_R + 60}
                  textAnchor="middle"
                  className="rm-empty-hint"
                >
                  No departments yet — add your first one →
                </text>
              )}
            </svg>

            <button
              className="rm-add-spoke-fab"
              onClick={() => setShowAddSpoke(true)}
              data-testid="roadmap-add-spoke"
              title="Add department"
            >
              <Plus size={16} /> Add Department
            </button>
          </div>

          {/* ─── Task Panel ─── */}
          <aside className="rm-task-panel" data-testid="roadmap-task-panel">
            {showingAll ? (
              <>
                <header className="rm-task-header">
                  <h3 className="rm-task-title" data-testid="roadmap-selected-label">
                    {stream.label} — all tasks
                  </h3>
                  <button
                    className="rm-icon-btn"
                    onClick={() => spokes[0] && setSelectedSpokeId(spokes[0].id)}
                    title="Back to single department view"
                    data-testid="roadmap-back-to-spoke"
                  >
                    <X size={14} />
                  </button>
                </header>

                <div className="rm-all-summary">
                  <span><strong>{counts.tasks}</strong> tasks</span>
                  <span className="rm-all-dot" />
                  <span><strong>{counts.in_progress}</strong> in progress</span>
                  <span className="rm-all-dot" />
                  <span><strong>{counts.done}</strong> done</span>
                </div>

                <div className="rm-all-list" data-testid="roadmap-all-list">
                  {spokes.length === 0 ? (
                    <div className="rm-task-empty">No tasks yet.</div>
                  ) : (
                    spokes.map((sp) => (
                      <section key={sp.id} className="rm-all-group">
                        <header
                          className="rm-all-group-header"
                          onClick={() => setSelectedSpokeId(sp.id)}
                          data-testid={`roadmap-all-group-${sp.id}`}
                        >
                          <span className="rm-all-group-label">{sp.label}</span>
                          <span className="rm-all-group-count">
                            {(sp.tasks || []).length}
                          </span>
                        </header>
                        <ul className="rm-task-list rm-task-list-compact">
                          {(sp.tasks || []).length === 0 ? (
                            <li className="rm-task-empty rm-task-empty-compact">
                              No tasks
                            </li>
                          ) : (
                            (sp.tasks || []).map((task) => {
                              const meta = STATUS_META[task.status] || STATUS_META.todo;
                              const Icon = meta.Icon;
                              return (
                                <li
                                  key={task.id}
                                  className={`rm-task-item rm-status-${task.status}`}
                                  data-testid={`roadmap-task-${task.id}`}
                                >
                                  <button
                                    className="rm-status-toggle"
                                    onClick={() => cycleTaskStatus(task)}
                                    title={`Status: ${meta.label} — click to advance`}
                                    style={{ color: meta.color }}
                                  >
                                    <Icon size={16} />
                                  </button>
                                  <span className="rm-task-text">{task.title}</span>
                                  <button
                                    className="rm-task-del"
                                    onClick={() => deleteTask(task.id)}
                                    title="Delete task"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </section>
                    ))
                  )}
                </div>
              </>
            ) : selectedSpoke ? (
              <>
                <header className="rm-task-header">
                  {renamingSpokeId === selectedSpoke.id ? (
                    <div className="rm-rename-row">
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameSpoke(selectedSpoke.id);
                          if (e.key === "Escape") {
                            setRenamingSpokeId(null);
                            setRenameDraft("");
                          }
                        }}
                        className="rm-input"
                        data-testid="roadmap-rename-input"
                      />
                      <button
                        className="rm-icon-btn rm-icon-btn-accent"
                        onClick={() => handleRenameSpoke(selectedSpoke.id)}
                        title="Save"
                        data-testid="roadmap-rename-save"
                      >
                        <Save size={14} />
                      </button>
                      <button
                        className="rm-icon-btn"
                        onClick={() => {
                          setRenamingSpokeId(null);
                          setRenameDraft("");
                        }}
                        title="Cancel"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <h3 className="rm-task-title" data-testid="roadmap-selected-label">
                        {selectedSpoke.label}
                      </h3>
                      <div className="rm-task-actions">
                        <button
                          className="rm-icon-btn"
                          onClick={() => {
                            setRenamingSpokeId(selectedSpoke.id);
                            setRenameDraft(selectedSpoke.label);
                          }}
                          title="Rename"
                          data-testid="roadmap-rename-btn"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          className="rm-icon-btn rm-icon-btn-danger"
                          onClick={() => handleDeleteSpoke(selectedSpoke.id)}
                          title="Delete department"
                          data-testid="roadmap-delete-spoke-btn"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </>
                  )}
                </header>

                <div className="rm-add-task-row">
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddTask();
                    }}
                    placeholder="Add a task and press Enter"
                    className="rm-input"
                    data-testid="roadmap-task-input"
                  />
                  <button
                    className="rm-add-task-btn"
                    onClick={handleAddTask}
                    disabled={busy || !newTaskTitle.trim()}
                    data-testid="roadmap-add-task-btn"
                  >
                    <Plus size={14} /> Add
                  </button>
                </div>

                <ul className="rm-task-list" data-testid="roadmap-task-list">
                  {(selectedSpoke.tasks || []).length === 0 ? (
                    <li className="rm-task-empty">No tasks yet.</li>
                  ) : (
                    (selectedSpoke.tasks || []).map((task) => {
                      const meta = STATUS_META[task.status] || STATUS_META.todo;
                      const Icon = meta.Icon;
                      return (
                        <li
                          key={task.id}
                          className={`rm-task-item rm-status-${task.status}`}
                          data-testid={`roadmap-task-${task.id}`}
                        >
                          <button
                            className="rm-status-toggle"
                            onClick={() => cycleTaskStatus(task)}
                            title={`Status: ${meta.label} — click to advance`}
                            data-testid={`roadmap-task-status-${task.id}`}
                            style={{ color: meta.color }}
                          >
                            <Icon size={16} />
                          </button>
                          <span className="rm-task-text">{task.title}</span>
                          <button
                            className="rm-task-del"
                            onClick={() => deleteTask(task.id)}
                            title="Delete task"
                            data-testid={`roadmap-task-del-${task.id}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              </>
            ) : (
              <div className="rm-empty-panel">
                <Map size={28} />
                <h3>Pick a department</h3>
                <p>Click a node on the spider to view its tasks, or add a new one.</p>
                <button
                  className="rm-add-task-btn rm-add-task-btn-big"
                  onClick={() => setShowAddSpoke(true)}
                  data-testid="roadmap-empty-add-spoke"
                >
                  <Plus size={14} /> Add Department
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* ── Add spoke modal ── */}
      {showAddSpoke && (
        <div
          className="rm-modal-backdrop"
          onClick={() => setShowAddSpoke(false)}
          data-testid="roadmap-add-spoke-modal"
        >
          <div className="rm-modal" onClick={(e) => e.stopPropagation()}>
            <header>
              <h3>New Department</h3>
              <button
                className="rm-icon-btn"
                onClick={() => setShowAddSpoke(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </header>
            <p className="rm-modal-sub">
              Adds a new spoke to{" "}
              <strong style={{ color: stream.accent }}>{stream.label}</strong>.
            </p>
            <input
              autoFocus
              type="text"
              value={newSpokeLabel}
              onChange={(e) => setNewSpokeLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddSpoke();
              }}
              placeholder="e.g. Engineering, Design, Marketing"
              className="rm-input"
              data-testid="roadmap-new-spoke-input"
            />
            <div className="rm-modal-actions">
              <button
                className="rm-add-task-btn rm-add-task-btn-big"
                onClick={handleAddSpoke}
                disabled={busy || !newSpokeLabel.trim()}
                data-testid="roadmap-new-spoke-save"
              >
                {busy ? <Loader2 size={14} className="rm-spin" /> : <Plus size={14} />}{" "}
                Add Department
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Label rendered tangentially outside the node — keeps it readable from any angle */
function SpokeLabel({ cx, cy, angle, radius, label }) {
  const pos = polar(cx, cy, radius, angle);
  // Right half → left-anchor; left half → right-anchor; near top/bottom → centre
  let anchor = "middle";
  if (angle > 12 && angle < 168) anchor = "start";
  else if (angle > 192 && angle < 348) anchor = "end";
  // Vertical nudge to prevent label clipping the node
  let dy = 4;
  if (angle < 30 || angle > 330) dy = -4;
  if (angle > 150 && angle < 210) dy = 14;
  return (
    <text
      x={pos.x}
      y={pos.y + dy}
      textAnchor={anchor}
      dominantBaseline="middle"
      className="rm-node-label"
    >
      {label.length > 24 ? `${label.slice(0, 22)}…` : label}
    </text>
  );
}
